import { ApolloLink, Observable, type FetchResult, type Operation } from '@apollo/client';
import { MOCK_RING_TEMPLATES } from './mockData';

/**
 * In-memory ApolloLink for backend-less hd_neo deployments.
 *
 * Replaces the network HttpLink when `VITE_USE_MOCK_API=true`.
 * Add new cases below as more queries get wired into the UI.
 *
 * Unknown operations fall through to an empty `data: null` response with a
 * console warning — this avoids hard-crashing the app but makes it obvious
 * something is missing.
 */
const resolvers: Record<string, (op: Operation) => unknown> = {
    RingTemplatesByGroupAndConfigurator: () => ({
        ringTemplatesByGroupAndConfigurator: MOCK_RING_TEMPLATES,
    }),
};

export const mockLink = new ApolloLink((operation) =>
    new Observable<FetchResult>((observer) => {
        const name = operation.operationName;
        const resolver = resolvers[name];

        const respond = () => {
            if (!resolver) {
                if (typeof console !== 'undefined') {
                    console.warn(
                        `[mockLink] no mock resolver for operation "${name}". Returning null.`,
                    );
                }
                observer.next({ data: null });
            } else {
                try {
                    observer.next({ data: resolver(operation) as Record<string, unknown> });
                } catch (err) {
                    observer.error(err);
                    return;
                }
            }
            observer.complete();
        };

        // Defer to next tick so callers behave the same as a real network round-trip.
        const id = setTimeout(respond, 0);
        return () => clearTimeout(id);
    }),
);
